import moment from "moment-timezone";
import { AlertTriangle } from "lucide-react";
import { useFormV2, FormField, FormGrid, FormActions } from "@/lib/form-v2";
import { Button } from "@/components/ui/button";
import { DialogClose } from "@/components/ui/dialog";
import { useListMarketZones } from "@/lib/api/generalContract";
import { CommonDestinationTimezone } from "@/lib/api/utils";
import { RiskMerchValue } from "@/lib/api/risk/merchValues";
import {
  merchValueSchemaConfig,
  buildMerchValueFieldMeta,
  defaultMerchValueFormValues,
  MerchValueFormValues,
} from "./form-schema";

// ---------------------------------------------------------------------------
// Month shortcuts
// ---------------------------------------------------------------------------

const MONTH_SHORTCUTS = [
  { label: "Jan", month: 0 },
  { label: "Feb", month: 1 },
  { label: "Mar", month: 2 },
  { label: "Apr", month: 3 },
  { label: "May", month: 4 },
  { label: "Jun", month: 5 },
  { label: "Jul", month: 6 },
  { label: "Aug", month: 7 },
  { label: "Sep", month: 8 },
  { label: "Oct", month: 9 },
  { label: "Nov", month: 10 },
  { label: "Dec", month: 11 },
];

// ---------------------------------------------------------------------------
// Overlap detection
// ---------------------------------------------------------------------------

function buildOverlapWarning(
  values: MerchValueFormValues,
  existingItems: RiskMerchValue[],
  excludeId: string | undefined,
): string | null {
  const { market_zone_id, commodity, crop_year, range_start, range_end } = values;
  if (!market_zone_id || !commodity || !crop_year || !range_start || !range_end) return null;
  if (!(range_start instanceof Date) || !(range_end instanceof Date)) return null;

  const formStart = moment(range_start).startOf("day");
  const formEnd = moment(range_end).startOf("day");
  if (!formStart.isValid() || !formEnd.isValid()) return null;

  for (const item of existingItems) {
    if (excludeId && item.id === excludeId) continue;
    if (item.market_zone_id !== market_zone_id) continue;
    if (item.commodity !== commodity) continue;
    if (item.crop_year !== crop_year) continue;

    const existStart = moment.tz(item.range_start, CommonDestinationTimezone).startOf("day");
    const existEnd = moment.tz(item.range_end, CommonDestinationTimezone).startOf("day");

    // Two ranges overlap when: formStart <= existEnd AND formEnd >= existStart
    if (formStart.isSameOrBefore(existEnd) && formEnd.isSameOrAfter(existStart)) {
      const label = `${existStart.format("MMM Do")} → ${existEnd.format("MMM Do")}`;
      return `This date range overlaps with an existing entry (${label}).`;
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// Form component
// ---------------------------------------------------------------------------

interface MerchValuesFormProps {
  initialValues?: Partial<MerchValueFormValues>;
  /** Whether the identifier fields (market_zone_id, commodity, crop_year) should be disabled (edit mode) */
  disableIdentifiers?: boolean;
  isSubmitting: boolean;
  onSubmit: (values: MerchValueFormValues) => void;
  submitLabel: string;
  /** All existing merch value items (used for overlap detection) */
  existingItems: RiskMerchValue[];
  /** When editing, exclude this record's id from the overlap check */
  excludeId?: string;
}

export function MerchValuesForm({
  initialValues,
  disableIdentifiers,
  isSubmitting,
  onSubmit,
  submitLabel,
  existingItems,
  excludeId,
}: MerchValuesFormProps) {
  const marketZonesQuery = useListMarketZones();

  const marketZoneOptions = (marketZonesQuery.data?.data || []).map(
    (zone: { id: string; dtn_market_zone: string }) => ({
      value: zone.id,
      label: zone.dtn_market_zone,
    }),
  );

  const fieldMeta = buildMerchValueFieldMeta(marketZoneOptions);

  // Override disabled for identifier fields in edit mode
  if (disableIdentifiers) {
    fieldMeta.market_zone_id = { ...fieldMeta.market_zone_id, disabled: true };
    fieldMeta.commodity = { ...fieldMeta.commodity, disabled: true };
    fieldMeta.crop_year = { ...fieldMeta.crop_year, disabled: true };
  }

  const form = useFormV2<MerchValueFormValues>(
    merchValueSchemaConfig,
    fieldMeta,
    { ...defaultMerchValueFormValues, ...initialValues },
  );

  const overlapWarning = buildOverlapWarning(form.values, existingItems, excludeId);

  const handleMonthShortcut = (monthIndex: number) => {
    const year = form.values.crop_year || new Date().getFullYear();
    const start = moment({ year, month: monthIndex, day: 1 });
    const end = start.clone().endOf("month").startOf("day");
    form.setFieldValues({
      range_start: start.toDate(),
      range_end: end.toDate(),
    } as Partial<MerchValueFormValues>);
    setTimeout(() => document.getElementById("value")?.focus(), 0);
  };

  const handleSubmit = () => {
    form.attemptSubmit();
    const result = form.validate();
    if (!result.success) return;
    onSubmit(form.values);
  };

  const renderField = (name: string) => {
    const props = form.getFieldProps(name);
    if (!props) return null;
    return (
      <FormField
        key={name}
        fieldProps={props}
        onChange={(v) => form.setFieldValue(name, v)}
        onBlur={() => form.touchField(name)}
      />
    );
  };

  return (
    <div className="flex flex-col gap-4">
      {renderField("merch_value_type")}
      <FormGrid cols={3}>
        {renderField("market_zone_id")}
        {renderField("commodity")}
        {renderField("crop_year")}
      </FormGrid>

      {/* Month shortcuts */}
      <div className="flex flex-wrap gap-1 items-center justify-center gap-3">
        {MONTH_SHORTCUTS.map((m) => (
          <Button
            key={m.month}
            type="button"
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => handleMonthShortcut(m.month)}
          >
            {m.label}
          </Button>
        ))}
      </div>

      <FormGrid cols={2}>
        {renderField("range_start")}
        {renderField("range_end")}
      </FormGrid>

      {overlapWarning && (
        <div className="flex items-center gap-2 rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>{overlapWarning}</span>
        </div>
      )}

      <FormGrid cols={2}>
        {renderField("value")}
        {renderField("note")}
      </FormGrid>
      <FormActions>
        <DialogClose asChild>
          <Button variant="outline" disabled={isSubmitting}>
            Cancel
          </Button>
        </DialogClose>
        <Button onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? "Saving..." : submitLabel}
        </Button>
      </FormActions>
    </div>
  );
}
